import { Link } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import { BriefcaseMedical, ClipboardList, Eye, LogOut, ShieldCheck, User, Users } from "lucide-react";
import { useMe } from "../hooks/useMe";

/** Landing page, ported from the legacy app. */
export function HomePage() {
  const auth = useAuth();
  const { me } = useMe();
  const showView = me?.role !== "paramedic";
  const signIn = () => void auth.signinRedirect();

  return (
    <main className="flex-1 flex flex-col min-h-screen">
      <header className="bg-white border-b-4 border-[var(--hmg-red)] shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <img src="/hmg-logo.png" alt="Dr Sulaiman Al Habib Medical Group" width={376} height={86} className="h-10 sm:h-12 md:h-16 w-auto" />
          {me ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link to="/profile" className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-slate-700 hover:text-slate-900 px-2 sm:px-3 py-2 rounded-lg">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">My Profile</span>
              </Link>
              <button
                type="button"
                onClick={() => void auth.signoutRedirect()}
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-[var(--hmg-red)] hover:text-white hover:bg-[var(--hmg-red)] border-2 border-[var(--hmg-red)] rounded-lg px-3 sm:px-5 py-2 sm:py-2.5 transition"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={signIn}
              className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-[var(--hmg-red)] hover:text-white hover:bg-[var(--hmg-red)] border-2 border-[var(--hmg-red)] rounded-lg px-3 sm:px-5 py-2 sm:py-2.5 transition flex-shrink-0"
            >
              <ShieldCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Staff Login</span>
              <span className="sm:hidden">Login</span>
            </button>
          )}
        </div>
      </header>

      <section className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-16 bg-gradient-to-b from-white via-slate-50 to-slate-100">
        <div className="max-w-5xl w-full">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[var(--hmg-red)] font-bold mb-3 sm:mb-4">HMG Emergency Medical Services</p>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-slate-900 mb-3 sm:mb-4 tracking-tight">EMS Operations</h1>
            <div className="w-20 h-1 bg-[var(--hmg-red)] mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card to="/unit-login" color="bg-[var(--hmg-gray)]" hover="hover:border-[var(--hmg-gray)]" text="text-[var(--hmg-gray)]" icon={<Users className="w-10 h-10" strokeWidth={1.8} />} title="Unit Login" desc="Register your unit (crew, ambulance & shift) at the start of the shift." cta="Log in unit" />
            <Card to="/checklist" color="bg-[var(--hmg-red)]" hover="hover:border-[var(--hmg-red)]" text="text-[var(--hmg-red)]" icon={<ClipboardList className="w-10 h-10" strokeWidth={1.8} />} title="Ambulance Checklist" desc="Complete a new daily readiness checklist." cta="Start checklist" />
            <Card to="/bag-checklist" color="bg-emerald-600" hover="hover:border-emerald-600" text="text-emerald-700" icon={<BriefcaseMedical className="w-10 h-10" strokeWidth={1.8} />} title="Bag Checklist" desc="Check the ACLS bag contents and quantities." cta="Start bag checklist" />
            {showView && (
              <Card to="/Checklists-Overview" color="bg-slate-900" hover="hover:border-slate-800" text="text-slate-900" icon={<Eye className="w-10 h-10" strokeWidth={1.8} />} title="View Checklists" desc="Supervisors and admins: sign in to view, search and print submitted checklists." cta="Sign in" />
            )}
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200 py-4">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-slate-500">&copy; {new Date().getFullYear()} Dr. Sulaiman Al Habib Medical Group</div>
      </footer>
    </main>
  );
}

function Card({ to, color, hover, text, icon, title, desc, cta }: { to: string; color: string; hover: string; text: string; icon: React.ReactNode; title: string; desc: string; cta: string }) {
  return (
    <Link to={to} className={`group relative overflow-hidden rounded-2xl bg-white border border-slate-200 ${hover} hover:shadow-2xl hover:-translate-y-1 transition-all p-6 sm:p-8 flex flex-col items-center text-center`}>
      <div className={`w-20 h-20 rounded-2xl ${color} text-white flex items-center justify-center mb-5 group-hover:scale-110 transition-transform shadow-lg`}>{icon}</div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">{title}</h2>
      <p className="text-slate-600 mb-4 text-sm">{desc}</p>
      <span className={`${text} font-semibold group-hover:translate-x-1 transition-transform`}>{cta} &rarr;</span>
    </Link>
  );
}
